import { Link as BaseLink, Box, Button } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import {
  CREATE_FIND_GROUP_MUTATION,
  GET_MY_FIND_GROUPS_QUERY,
} from '../../gql';
import { formatDate } from '../../helpers';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMaterialReactTableLocalization } from '../../i18n';

export interface FindGroupRow {
  id: string;
  name: string;
  description?: string | null;
  createdAt?: string | null;
  createdBy?: string | null;
  createdByName?: string | null;
  createdByEmail?: string | null;
  shared?: boolean | null;
  level?: number | null;
}

const gridStyles = { marginBottom: 6, display: 'grid' };

export function FindGroupsTable({
  rows,
  loading = false,
}: {
  rows: FindGroupRow[];
  loading?: boolean;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const localization = useMaterialReactTableLocalization();
  const columns = useMemo<MRT_ColumnDef<FindGroupRow>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 140,
        Cell: ({ cell, renderedCellValue }) => (
          <BaseLink
            underline="none"
            component={Link}
            to={`/finds/groups/${cell.getValue()}`}
          >
            <span>{renderedCellValue}</span>
          </BaseLink>
        ),
      },
      {
        accessorKey: 'name',
        header: intl.formatMessage({
          id: 'common.name',
          defaultMessage: 'Name',
        }),
        size: 200,
      },
      {
        accessorKey: 'description',
        header: intl.formatMessage({
          id: 'form.description',
          defaultMessage: 'Description',
        }),
        size: 260,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? '-',
      },
      {
        accessorKey: 'shared',
        header: intl.formatMessage({
          id: 'finds.group.ownership',
          defaultMessage: 'Ownership',
        }),
        size: 130,
        Cell: ({ cell }) =>
          cell.getValue()
            ? intl.formatMessage({
                id: 'finds.group.sharedWithMe',
                defaultMessage: 'Shared with me',
              })
            : intl.formatMessage({
                id: 'finds.group.myGroup',
                defaultMessage: 'My group',
              }),
      },
      {
        accessorFn: (row) => row.createdAt && new Date(row.createdAt),
        header: intl.formatMessage({
          id: 'finds.group.dateCreated',
          defaultMessage: 'Date created',
        }),
        size: 130,
        Cell: ({ cell }) => formatDate(cell.getValue<Date | null>()) || '-',
      },
    ],
    [intl]
  );
  const [
    createGroup,
    { loading: creating },
  ] = useMutation(CREATE_FIND_GROUP_MUTATION, {
    refetchQueries: [{ query: GET_MY_FIND_GROUPS_QUERY }],
  });

  const handleCreate = async () => {
    const result = await createGroup({
      variables: {
        group: {
          name: intl.formatMessage({
            id: 'finds.group.newName',
            defaultMessage: 'New group',
          }),
        },
      },
    });
    const payload = result.data?.createFindGroup;
    if (payload?.__typename === 'CreateFindGroupSuccess') {
      navigate(`/finds/groups/${payload.group.id}`);
    }
  };

  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization,
    enableToolbarInternalActions: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
      density: 'compact',
    },
    state: { isLoading: loading },
    muiPaginationProps: { showRowsPerPage: false },
    muiTablePaperProps: { sx: gridStyles },
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: 'flex', gap: '1rem', p: '4px' }}>
        <Button
          color="primary"
          variant="contained"
          onClick={handleCreate}
          disabled={creating}
        >
          <FormattedMessage
            id="finds.group.create"
            defaultMessage="Create group"
          />
        </Button>
      </Box>
    ),
  });

  return <MaterialReactTable table={table} />;
}
